import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { KeyboardEvent, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Iconify from 'src/components/iconify';
import { translateKey } from 'src/locales/translate';
import StockProductPicker from 'src/sections/@dashboard/warehouse/components/StockProductPicker';
import {
  StockMeasure,
  parseStockQuantity,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { WarehouseProductDto } from 'woofwoof-api';
import {
  TreatmentMaterialValue,
  formatStockQuantity,
  measureShortLabel,
} from './associatedMedicineStock';
import { selectTestId } from './medication-form/selectTestId';

interface TreatmentMaterialsEditorProps {
  value: TreatmentMaterialValue[];
  onChange: (next: TreatmentMaterialValue[]) => void;
  disabled?: boolean;
}

const MEASURES: StockMeasure[] = ['UNIT', 'PACKAGE'];

/**
 * Controlled (non-RHF) list of non-drug materials beside a treatment form,
 * built like `AssociatedMedicinesEditor`: drugs go through Medications, so
 * only non-drug Products are offered (ADR 0026). A saved line keeps its id,
 * so its movements stay; to change one, remove it and add it again.
 */
export default function TreatmentMaterialsEditor({
  value,
  onChange,
  disabled = false,
}: TreatmentMaterialsEditorProps): React.ReactElement {
  const [product, setProduct] = useState<WarehouseProductDto | null>(null);
  /** The typed text: comma or dot, at most three decimals (Phase 1). */
  const [quantityText, setQuantityText] = useState('');
  const [measure, setMeasure] = useState<StockMeasure>('UNIT');
  const quantity = parseStockQuantity(quantityText);
  const quantityInvalid = quantityText.trim() !== '' && quantity === null;
  const canAdd = Boolean(product) && quantity !== null;

  const add = (): void => {
    if (!product || quantity === null) return;
    onChange([
      ...value,
      {
        productId: product.id,
        productName: product.name,
        quantity,
        measure,
        clientKey: uuidv4(),
      },
    ]);
    setProduct(null);
    setQuantityText('');
    setMeasure('UNIT');
  };

  const handleQuantityKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
  ): void => {
    // Enter would submit the treatment form this editor sits in.
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add();
  };

  return (
    <Stack spacing={1.5} data-testid="treatment-materials">
      <Typography variant="subtitle2">
        {translateKey('warehouse.clinical.materials')}
      </Typography>

      {!disabled && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Box sx={{ flex: 2 }}>
            <StockProductPicker
              kind="NON_DRUG"
              value={product?.id ?? ''}
              onChange={setProduct}
              label={translateKey('warehouse.clinical.material')}
              testId="treatment-material-product"
            />
          </Box>
          <TextField
            name="materialQuantity"
            label={translateKey('warehouse.clinical.quantity')}
            value={quantityText}
            onChange={(event) => setQuantityText(event.target.value)}
            onKeyDown={handleQuantityKeyDown}
            error={quantityInvalid}
            helperText={
              quantityInvalid
                ? translateKey('warehouse.errors.INVALID_QUANTITY')
                : undefined
            }
            // Phase 1's quantity field: a comma or a dot, parsed on add.
            inputProps={{
              inputMode: 'decimal',
              'data-testid': 'treatment-material-quantity',
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            name="materialMeasure"
            label={translateKey('warehouse.clinical.measure')}
            value={measure}
            onChange={(event) =>
              setMeasure(
                MEASURES.find((option) => option === event.target.value) ??
                  'UNIT',
              )
            }
            SelectProps={{
              SelectDisplayProps: selectTestId('treatment-material-measure'),
            }}
            sx={{ flex: 1 }}
          >
            {MEASURES.map((option) => (
              <MenuItem key={option} value={option}>
                {measureShortLabel(option)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            onClick={add}
            disabled={!canAdd}
            data-testid="treatment-material-add"
            sx={{ flexShrink: 0 }}
          >
            {translateKey('warehouse.clinical.materialAdd')}
          </Button>
        </Stack>
      )}

      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translateKey('warehouse.clinical.materialsEmpty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {value.map((material, index) => (
              <TableRow
                // Its own key, never its position: removing a line above
                // must not hand this row to another line.
                key={material.id ?? material.clientKey ?? index}
                data-testid="treatment-material-row"
              >
                <TableCell>{material.productName}</TableCell>
                <TableCell>
                  {`${formatStockQuantity(
                    material.quantity,
                  )} ${measureShortLabel(material.measure)}`}
                </TableCell>
                <TableCell align="right">
                  {!disabled && (
                    <IconButton
                      color="error"
                      aria-label={translateKey(
                        'warehouse.clinical.materialRemove',
                      )}
                      data-testid="treatment-material-remove"
                      onClick={() =>
                        onChange(value.filter((_, i) => i !== index))
                      }
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
